import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import styles from "./stylesSortingTable.module.css";
import PopularTabsRow from "./SortingTableRow/SortingTableRow";

const SortingTable = ({
  columns,
  data,
  setData,
  toggleStatus,
  removeItem,
  onRename,
  changeOrder,
  isLoading = false,
  canEdit = true,
  enablePositionSelect = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = async (event) => {
    if (!canEdit) return;

    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = data.findIndex((item) => item.id === active.id);
      const newIndex = data.findIndex((item) => item.id === over.id);

      // Create the new array with reordered items
      const reorderedItems = arrayMove(data, oldIndex, newIndex);

      // Update all order numbers to reflect the new positions
      const updatedItems = reorderedItems.map((item, index) => ({
        ...item,
        order: (index + 1).toString(),
      }));

      // Update local state first for immediate UI feedback
      setData(updatedItems);

      // Call the backend API to update the order
      await changeOrder(updatedItems);
    }
  };

  const handlePositionChange = async (oldIndex, position) => {
    const newIndex = position - 1;

    if (!canEdit || oldIndex === newIndex) return;

    const reorderedItems = arrayMove(data, oldIndex, newIndex);
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      order: (index + 1).toString(),
    }));

    setData(updatedItems);
    await changeOrder(updatedItems);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={canEdit ? handleDragEnd : undefined}
    >
      <SortableContext
        items={data.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
        disabled={!canEdit}
      >
        <table
          className={`${styles.sortingTable} ${isLoading ? styles.tableLoading : ""} popular-tabs-table`}
        >
          <thead>
            <tr className={styles.headerRow}>
              {columns.map((column) => (
                <th key={column} className={styles.headerCell}>
                  <div className={styles.headerCellContent}>
                    <span>{column}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <PopularTabsRow
                key={item.id}
                item={item}
                order={index + 1}
                toggleStatus={toggleStatus}
                removeItem={removeItem}
                onRename={onRename}
                canEdit={canEdit}
                enablePositionSelect={enablePositionSelect}
                positionCount={data.length}
                onPositionChange={(position) =>
                  handlePositionChange(index, position)
                }
              />
            ))}
          </tbody>
        </table>
      </SortableContext>
    </DndContext>
  );
};

export default SortingTable;
